import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { CurrencySchema, IsoDateSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * PRINTED DOCUMENTS — DECISION.md §2.13.
 *
 * Three routes and no fourth: ASK for a document, POLL it, DOWNLOAD it.
 *
 * There is deliberately no "render this and give me the bytes" route. §2.13:
 * "PDF generation should be a job, not a request." A synchronous route would
 * hold a request thread across a Chromium page load, which is the failure mode
 * the decision names — so the POST returns an id in milliseconds and the
 * worker does the work.
 */
export const DOCUMENT_MESSAGES = {
    notReady: 'This document is still being produced. Try again in a moment.',
    renderFailed: 'This document could not be produced. Ask for it again, or contact support.',
    notFound: 'Document not found.',
    gridTooLarge: 'That report is too large to print. Narrow the period and try again.',
};
/**
 * What the product prints. The first three are DECISION.md §2.13's originals;
 * the eight that follow are wave 2 of `specs/erp-gap-analysis.md` ("document
 * toolbar parity"): every transactional editor gets the same Print → PDF the
 * sales invoice has had, from the same renderer and the same `documents` row.
 *
 * The list is closed in three places that must agree — here, the CHECK on
 * `documents.kind`, and `DOCUMENT_KINDS` in @palaccount/documents — so adding
 * a kind is a contract change, a migration and a template, in that order.
 */
export const DocumentKindSchema = z.enum([
    'sales_invoice',
    'partner_statement',
    'report',
    // Wave 2 — sales side.
    'quotation',
    'sales_order',
    'delivery_note',
    'credit_note',
    // Wave 2 — purchasing side.
    'purchase_order',
    'purchase_invoice',
    'goods_receipt',
    'purchase_return',
    // Wave 5E — PUR-06's shipment: stages, lines at landed cost, charges.
    'import_order',
]);
/**
 * The eight wave-2 kinds that are "a record the company issues", printed from
 * the row by id — the sales invoice's own shape (`subjectId` + nothing else),
 * for the sales invoice's own reason: what the paper says must come from the
 * server, never from a figure the browser typed.
 */
export const SUBJECT_DOCUMENT_KINDS = [
    'quotation',
    'sales_order',
    'delivery_note',
    'credit_note',
    'purchase_order',
    'purchase_invoice',
    'goods_receipt',
    'purchase_return',
    // Wave 5E — the import order, printed from its row by id like the rest.
    'import_order',
];
export const DocumentLocaleSchema = z.enum(['en', 'ar']);
export const DocumentStateSchema = z.enum(['pending', 'ready', 'failed']);
/**
 * The report grid, as displayed.
 *
 * WHY THE CLIENT SENDS THIS, when the invoice and the statement send only an
 * id. The rule the two obey is the same rule, applied honestly:
 *
 *  - An INVOICE is a record the company issues. What it says must come from
 *    the server, or a customer could print a total they typed. Hence
 *    `subjectId` and nothing else.
 *  - A REPORT is a VIEW. BR-DSH-10-24 says "the export carries exactly the
 *    columns/rows on screen", and what is on screen is composed in the browser
 *    — the visible-columns picker (DSH-10 band 6 / R13), the reader's own
 *    language, the money formatter. Re-deriving that server-side would be a
 *    SECOND implementation of the report screen, and the first time the two
 *    disagreed, the PDF would be the one that was wrong.
 *
 * The precedent is `email_outbox`, which stores the RENDERED subject and body
 * for the same reason and states it in its own migration: a job payload is
 * ids only (§5.9), but the durable ROW may hold what was actually going out.
 * The grid lives on `documents.params`, not in the pg-boss payload.
 *
 * It is bounded, because it is a request body: an unbounded grid is a way to
 * make the worker render a hundred-megabyte page.
 */
const ReportCellSchema = z.string().max(200);
export const ReportGridSchema = z
    .object({
    title: z.string().min(1).max(120),
    /**
     * The window the report covers — ABSENT on a report that declares itself
     * as-of-now.
     *
     * BR-DSH-10-02 requires every report to accept a from and a to date and
     * every figure to cover exactly that window. DSH-07 (Inventory) is exempt
     * under delegated ruling A14 (25 Aug 2026, option (a)): it is a
     * point-in-time picture of the stock held now, its date boxes are removed,
     * and it sends no window here. BR-DSH-10-16's "the window covered" is then
     * satisfied by the run time the heading block already prints — the as-of
     * moment IS the window for a report that has no other.
     *
     * Optional TOGETHER, never one of the two: a heading showing a start and
     * no end (or the reverse) describes no period at all, and is the shape a
     * reader would most easily misread as a full window.
     */
    from: IsoDateSchema.optional(),
    to: IsoDateSchema.optional(),
    cards: z
        .object({
        label: z.string().min(1).max(80),
        value: z.string().min(1).max(60),
        changePct: z.string().max(12).nullable().optional(),
    })
        .strict()
        .array()
        .max(8),
    columns: ReportCellSchema.array().min(1).max(24),
    /** Indexes into `columns` that hold a figure: right-aligned, LTR island. */
    numericColumns: z.number().int().nonnegative().max(23).array().max(24).optional(),
    rows: ReportCellSchema.array().array().max(5000),
    /**
     * BR-DSH-10-16 — the printed heading block carries "the filters applied".
     * Already-labelled pairs in the reader's language, for the same reason the
     * columns and rows arrive rendered: the screen is the only party that
     * knows which filters were in force and what they are called. A report
     * with none in force (DSH-08 has no More filters band) sends none.
     */
    filters: z
        .object({ label: z.string().min(1).max(80), value: z.string().min(1).max(120) })
        .strict()
        .array()
        .max(12)
        .optional(),
    /**
     * DSH-10 Band 6 (BR-DSH-10-16): "The printed heading block must name the
     * configuration if one is in use." Null / absent when the reader is not on
     * a saved configuration, or has diverged from the one they applied.
     */
    configName: z.string().min(1).max(120).nullable().optional(),
})
    .strict()
    // The pair is a window or it is nothing. Asserted rather than described,
    // because a comment saying "never one of the two" is a claim and this is a
    // check — and the half-window is exactly what a page part-way through
    // adopting the as-of-now shape would send.
    .refine((grid) => (grid.from === undefined) === (grid.to === undefined), {
    message: 'A report grid carries both window dates or neither.',
    path: ['to'],
});
/**
 * Wave 2's eight record kinds share one request shape: the kind, the
 * recipient's language, and the id of the row to print. Spelled out as eight
 * literal members rather than one `z.enum` member so the union stays a plain
 * discriminated union on `kind` — the toolbar sends `{ kind, subjectId, locale }`
 * and the server narrows on the literal, exactly as it does for the invoice.
 */
function subjectDocumentRequest(kind) {
    return z
        .object({
        kind: z.literal(kind),
        locale: DocumentLocaleSchema,
        /** The id of the record the paper is about; the server reads the rest. */
        subjectId: z.uuid(),
    })
        .strict();
}
/**
 * The ask. Exactly one shape per kind, discriminated on `kind`, so a caller
 * cannot send a report grid for an invoice and have it silently ignored.
 */
export const CreateDocumentRequestSchema = z.discriminatedUnion('kind', [
    subjectDocumentRequest('quotation'),
    subjectDocumentRequest('sales_order'),
    subjectDocumentRequest('delivery_note'),
    subjectDocumentRequest('credit_note'),
    subjectDocumentRequest('purchase_order'),
    subjectDocumentRequest('purchase_invoice'),
    subjectDocumentRequest('goods_receipt'),
    subjectDocumentRequest('purchase_return'),
    subjectDocumentRequest('import_order'),
    z
        .object({
        kind: z.literal('sales_invoice'),
        locale: DocumentLocaleSchema,
        invoiceId: z.uuid(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('partner_statement'),
        locale: DocumentLocaleSchema,
        partnerId: z.uuid(),
        /** Which title the sheet carries; a partner can be both (MD-01). */
        side: z.enum(['customer', 'supplier']),
        /**
         * BR-CRM-04-18's window, carried onto the paper.
         *
         * The invoice sends only an id because what an invoice says must come
         * from the server; a statement PERIOD is not a figure, it is which
         * question was asked — and the screen is where it was asked. Without
         * these two the printed sheet would silently be the default window
         * while the reader was looking at another one, and its closing balance
         * would disagree with the screen's. That is the one bug this module
         * cannot have (see `partner-statement.ts`'s header).
         *
         * Optional: a caller that names no window gets the tenant's default,
         * which is what the screen itself started from.
         */
        from: IsoDateSchema.optional(),
        to: IsoDateSchema.optional(),
        /**
         * Which currency's statement the screen was showing (balances are per
         * currency); absent = the partner's own. Carried for the same reason as
         * the window: the paper must be the statement the reader was looking at.
         */
        currency: CurrencySchema.optional(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('report'),
        locale: DocumentLocaleSchema,
        grid: ReportGridSchema,
    })
        .strict(),
]);
/**
 * What a caller may know about a document. Not the bytes, and not `params`:
 * the poll is a state machine, and the download is a download.
 */
export const DocumentSchema = z
    .object({
    id: z.uuid(),
    kind: DocumentKindSchema,
    locale: DocumentLocaleSchema,
    state: DocumentStateSchema,
    filename: z.string(),
    byteSize: z.number().int().nullable(),
    /** The renderer's own words on a failure; null otherwise. */
    lastError: z.string().nullable(),
    createdAt: z.string(),
    completedAt: z.string().nullable(),
})
    .strict();
export const documentsContract = c.router({
    create: {
        method: 'POST',
        path: '/api/documents',
        body: CreateDocumentRequestSchema,
        responses: {
            202: DocumentSchema,
            400: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Ask for a document — an invoice, a statement, a report, or any of the eight wave-2 record kinds. Returns immediately; the worker renders it.',
    },
    get: {
        method: 'GET',
        path: '/api/documents/:documentId',
        pathParams: z.object({ documentId: z.uuid() }).strict(),
        responses: { 200: DocumentSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Poll a document until it is ready.',
    },
    download: {
        method: 'GET',
        path: '/api/documents/:documentId/content',
        pathParams: z.object({ documentId: z.uuid() }).strict(),
        responses: {
            // The bytes. ts-rest types the body as unknown here because it is not
            // JSON; the controller sets Content-Type and Content-Disposition.
            200: c.otherResponse({ contentType: 'application/pdf', body: z.unknown() }),
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Download the artifact. TODO(deploy): once §2.15 R2 exists this issues a short-lived presigned URL instead of proxying bytes.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=documents.js.map